// Centralized application configuration and URLs
// This module provides a single place to access app constants and environment
// variables with development-safe fallbacks.

export type AppRelease = {
  version: string
  buildTime: string
}

export type AppUrls = {
  knowledgeBase: string
  status: string
  supportEmail: string
  twitter?: string
  linkedin?: string
  github?: string
}

export type AppEnv = {
  sentryDsn: string
  sentryTraceRate: number
  trackingEnabled: boolean
  trackUrl: string
  trackId: string
  baseUrl: string
  turnstileSiteKey?: string
  stripePublishableKey?: string
  gtagId?: string
  gadsConversionRegister?: string
  gadsConversionK8s?: string
  gadsConversionPayment?: string
  gadsConversionFreeClaim?: string
  gadsConversionVserver?: string
}

function toBoolean(val: unknown): boolean {
  if (typeof val === 'boolean') return val
  if (typeof val === 'number') return val !== 0
  if (typeof val === 'string') return ['1', 'true', 'yes', 'on'].includes(val.toLowerCase())
  return false
}

function num(val: unknown, fallback: number): number {
  const n = Number(val)
  return Number.isFinite(n) ? n : fallback
}

// Safe access wrapper for Vite envs
const env = import.meta.env as Record<string, unknown>

const version = (env.VITE_RELEASE as string) || 'dev'
const buildTime = new Date().toISOString()

export const appName = 'ITSH'

export const release: AppRelease = {
  version,
  buildTime
}

export const urls: AppUrls = {
  // allow overriding via env if provided in deployments
  knowledgeBase: (env.VITE_SUPPORT_KB_URL as string) || 'https://sup.itsh.dev/help/de-de',
  status: (env.VITE_STATUS_URL as string) || 'https://status.itsh.dev',
  supportEmail: (env.VITE_SUPPORT_EMAIL as string) || 'mailto:service@itsh.dev',
  twitter: (env.VITE_SOCIAL_TWITTER as string) || '',
  linkedin: (env.VITE_SOCIAL_LINKEDIN as string) || '',
  github: (env.VITE_SOCIAL_GITHUB as string) || ''
}

export const appEnv: AppEnv = {
  sentryDsn: (env.VITE_SENTRY_DSN as string) || '',
  sentryTraceRate: num(env.VITE_SENTRY_TRACE_RATE, 0),
  trackingEnabled: toBoolean(env.VITE_TRACKING),
  trackUrl: (env.VITE_TRACK_URL as string) || '',
  trackId: (env.VITE_TRACK_ID as string) || '',
  baseUrl: (env.VITE_BASE_URL as string) || 'http://localhost:8000',
  turnstileSiteKey: env.VITE_TURNSTILE_SITE_KEY as string | undefined,
  stripePublishableKey: env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined,
  gtagId: env.VITE_GTAG_ID as string | undefined,
  gadsConversionRegister: env.VITE_GADS_CONVERSION_REGISTER as string | undefined,
  gadsConversionK8s: env.VITE_GADS_CONVERSION_K8S as string | undefined,
  gadsConversionPayment: env.VITE_GADS_CONVERSION_PAYMENT as string | undefined,
  gadsConversionFreeClaim: env.VITE_GADS_CONVERSION_FREE_CLAIM as string | undefined,
  gadsConversionVserver: env.VITE_GADS_CONVERSION_VSERVER as string | undefined
}

/**
 * Supported languages for the application.
 * Used in language selectors across account settings and other forms.
 */
export const supportedLanguages = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'English', flag: '🇬🇧' }
] as const

export type SupportedLanguageCode = (typeof supportedLanguages)[number]['code']

export const appConfig = {
  name: appName,
  release,
  urls,
  env: appEnv,
  supportedLanguages
}
